'use client';

import { Box, Flex, IconButton, Image } from '@chakra-ui/react';
import { PrimaryRailIcon } from './PrimaryRailIcon';
import NextLink from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useSyncExternalStore, type ReactNode, type PointerEventHandler } from 'react';
import { Tooltip } from '@/components/ui/tooltip';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import {
  getWorkspaceApprovalPendingCount,
  subscribeWorkspaceApprovalPendingCount,
} from '@/lib/delivery-approvals/client-events';
import { useAppRail } from './AppRailContext';
import { getPrimarySection, type PrimarySection } from './primary-navigation';
import { AppAccountMenu } from './Sidebar';
import { useEditorSessions } from './EditorSessions';
import { editorSessionHref } from './editor-sessions';
import { isCanvasEnabledClient } from '@/lib/canvas/feature-flags';
import { isGitHubWebsiteEnabledClient, isGitHubLivePreviewEnabledClient } from '@/lib/github/feature-flags';

const destinations = [
  { id: 'chat', label: 'Chat', href: '/app/workspace?new_chat=true' },
  {
    id: 'work',
    label: 'Work',
    href: '/app/workspace?mode=tasks&tab=tasks',
  },
  { id: 'agents', label: 'Agents', href: '/app/agents' },
  { id: 'data', label: 'Data Explorer', href: '/app/data-explorer' },
  { id: 'site', label: 'Site', href: '/app/workspace?editor=website&new_chat=true' },
  { id: 'canvas', label: 'Canvas', href: '/app/workspace?editor=canvas&new_chat=true' },
  { id: 'library', label: 'Library', href: '/app/docs' },
  { id: 'tools', label: 'Plugins', href: '/app/plugins' },
] as const;

const subscribeApprovalCount = (onChange: () => void) =>
  subscribeWorkspaceApprovalPendingCount(() => onChange());

/** All shell states share the same geometry and destination list. */
export function PrimaryRail({
  initialIsAdmin = false,
  authPreview = false,
  onboardingLocked = false,
}: {
  initialIsAdmin?: boolean;
  authPreview?: boolean;
  onboardingLocked?: boolean;
}) {
  if (authPreview || onboardingLocked) {
    return (
      <PrimaryRailView
        section="chat"
        lockedReason={authPreview ? 'Sign in to continue' : 'Finish project setup to continue'}
      />
    );
  }
  return <AuthenticatedPrimaryRail initialIsAdmin={initialIsAdmin} />;
}

function AuthenticatedPrimaryRail({ initialIsAdmin }: { initialIsAdmin: boolean }) {
  const { user } = useAuth();
  const pathname = usePathname();
  const params = useSearchParams();
  const { currentProject } = useProject();
  const { sessions: editorSessions } = useEditorSessions();
  const projectId = currentProject?.id;
  const approvalCount = useSyncExternalStore(
    subscribeApprovalCount,
    useCallback(
      () => (projectId ? (getWorkspaceApprovalPendingCount(projectId) ?? 0) : 0),
      [projectId]
    ),
    () => 0
  );
  const {
    editorAvailability,
    handleRailPointerEnter,
    handleRailPointerLeave,
    closeRailHover,
  } = useAppRail();
  return (
    <PrimaryRailView
      section={getPrimarySection(pathname, params)}
      editorAvailability={editorAvailability?.userId === user?.uid && editorAvailability?.projectId === projectId ? editorAvailability ?? undefined : undefined}
      editorHrefs={{
        site: editorSessionHref('website', editorSessions.find(item => item.editor === 'website')),
        canvas: editorSessionHref('canvas', editorSessions.find(item => item.editor === 'canvas')),
      }}
      approvalCount={approvalCount}
      onPointerEnter={handleRailPointerEnter}
      onPointerLeave={handleRailPointerLeave}
      onPointerCancel={closeRailHover}
      account={
        <AppAccountMenu
          isAdmin={initialIsAdmin}
          railOwned={false}
          trigger={
            <IconButton
              aria-label="Account and Settings"
              title="Account and Settings"
              variant="ghost"
              boxSize="11"
              flexShrink={0}
              borderRadius="14px"
            >
              <PrimaryRailIcon name="settings" filled={getPrimarySection(pathname, params) === 'settings'} />
            </IconButton>
          }
        />
      }
    />
  );
}

export function PrimaryRailView({
  section,
  lockedReason,
  approvalCount = 0,
  onPointerEnter,
  onPointerLeave,
  onPointerCancel,
  account,
  editorHrefs,
  editorAvailability,
}: {
  section: PrimarySection;
  lockedReason?: string;
  approvalCount?: number;
  onPointerEnter?: PointerEventHandler<HTMLDivElement>;
  onPointerLeave?: PointerEventHandler<HTMLDivElement>;
  onPointerCancel?: PointerEventHandler<HTMLDivElement>;
  account?: ReactNode;
  editorHrefs?: { site: string; canvas: string };
  editorAvailability?: { site: boolean; canvas: boolean };
}) {
  const logo = (
    <Image src="/logo_mini.svg" alt="" boxSize="6" _dark={{ filter: 'brightness(0) invert(1)' }} />
  );
  return (
    <Flex
      as="nav"
      aria-label="Main navigation"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerCancel={onPointerCancel}
      display={{ base: 'none', lg: 'flex' }}
      direction="column"
      overflowY="auto"
      minH={0}
      align="center"
      w="60px"
      flexShrink={0}
      py="3"
      gap="2"
      bg="transparent"
      zIndex={4}
    >
      {lockedReason ? (
        <IconButton
          aria-label="Attune home"
          disabled
          variant="ghost"
          boxSize="10"
          flexShrink={0}
          mb="3"
        >
          {logo}
        </IconButton>
      ) : (
        <IconButton
          asChild
          aria-label="Attune home"
          variant="ghost"
          boxSize="10"
          flexShrink={0}
          mb="3"
        >
          <NextLink href="/app/workspace?new_chat=true">{logo}</NextLink>
        </IconButton>
      )}
      {destinations.map(({ id, label, href }) => {
        if (id === 'site' && (!editorAvailability?.site || !isGitHubWebsiteEnabledClient() || !isGitHubLivePreviewEnabledClient())) return null;
        if (id === 'canvas' && (!editorAvailability?.canvas || !isCanvasEnabledClient())) return null;
        const destination = id === 'site' || id === 'canvas' ? editorHrefs?.[id] || href : href;
        const accessibleLabel =
          id === 'work' && approvalCount > 0 ? `Work, ${approvalCount} pending approvals` : label;
        const icon = (
          <>
            <PrimaryRailIcon name={id} filled={section === id} />
            {id === 'work' && approvalCount > 0 ? (
              <Box
                aria-hidden="true"
                position="absolute"
                top="1.5"
                right="1.5"
                boxSize="2"
                borderRadius="full"
                bg="status.warning.fg"
              />
            ) : null}
          </>
        );
        const styles = {
          position: 'relative',
          variant: 'ghost',
          boxSize: '11',
          flexShrink: 0,
          borderRadius: '14px',
          color: section === id ? 'ui.text.primary' : 'ui.text.tertiary',
          bg: section === id ? 'ui.surface.selected' : 'transparent',
          _hover: { bg: 'ui.surface.selected', color: 'ui.text.primary' },
          _focusVisible: { outline: '2px solid', outlineColor: 'accent.focusRing' },
        } as const;
        return (
          <Tooltip
            key={id}
            content={lockedReason ? `${label} · ${lockedReason}` : label}
            positioning={{ placement: 'right' }}
          >
            {lockedReason ? (
              <IconButton
                {...styles}
                aria-label={accessibleLabel}
                aria-disabled="true"
                tabIndex={-1}
                cursor="default"
              >
                {icon}
              </IconButton>
            ) : (
              <IconButton {...styles} asChild aria-label={accessibleLabel}>
                <NextLink href={destination} aria-current={section === id ? 'true' : undefined}>
                  {icon}
                </NextLink>
              </IconButton>
            )}
          </Tooltip>
        );
      })}
      <Flex mt="auto" pt="4" direction="column" align="center" gap="2">
        {account || (
          <IconButton
            aria-label="Account and Settings"
            disabled
            variant="ghost"
            boxSize="11"
            borderRadius="14px"
          >
            <PrimaryRailIcon name="settings" />
          </IconButton>
        )}
      </Flex>
    </Flex>
  );
}
